import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { organizationAPI } from '../services/api';
import { SelectOption } from '../components/SearchableMultiSelect';
import { unwrapResponse } from '../utils/unwrapResponse';
import { queryKeys } from './queries/queryKeys';

interface RawOrgMember {
  userId: number;
  userName?: string;
  displayName?: string;
  email?: string;
  isSelf?: boolean;
}

interface OrgMembersResponse {
  members: RawOrgMember[];
}

export interface UseOrganizationMembersOptions {
  organizationId?: string;
  excludeUserIds?: number[];
  excludeSelf?: boolean;
}

export interface UseOrganizationMembersResult {
  members: SelectOption[];
  loading: boolean;
  error: string;
  reload: () => Promise<void>;
}

async function fetchOrgMembers(organizationId: string): Promise<OrgMembersResponse> {
  const response = await organizationAPI.getMembers(organizationId);
  return unwrapResponse<any>(response.data);
}

/**
 * Spec 294 category 3 migration (2026-08-03). Follow-up (2026-08-03,
 * post-review): originally shared its cache entry with
 * `hooks/queries/useOrgMembers.ts`'s `useOrgMembersQuery` (same
 * `organizationAPI.getMembers(orgId)` endpoint, same param, same response
 * shape). A skeptical review flagged that as a dormant options-collision
 * footgun -- this hook wants `retry: false`; `useOrgMembersQuery` sets
 * `staleTime: 300_000` for its own unrelated consumer (`useSettingsCore.ts`)
 * -- TanStack Query has exactly one options set per cache entry, so if the
 * two were ever co-mounted, whichever mounted first would silently win for
 * both. Now on its own key (`queryKeys.organizations.membersForSelect`,
 * see that key's own comment) to remove the risk entirely; the org-member
 * mutations (useAddOrgMember/useUpdateMemberRole/useRemoveOrgMember in
 * hooks/queries/useOrgMembers.ts) invalidate both keys, so this hook still
 * gets refreshed for free on membership changes.
 */
export const useOrganizationMembers = ({
  organizationId,
  excludeUserIds = [],
  excludeSelf = true,
}: UseOrganizationMembersOptions): UseOrganizationMembersResult => {
  // Stabilize excludeUserIds by value (prevents infinite re-render loop)
  const excludeKey = JSON.stringify(excludeUserIds);
  const stableExcludeIds: number[] = useMemo(() => excludeUserIds, [excludeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useQuery({
    queryKey: queryKeys.organizations.membersForSelect(organizationId || ''),
    queryFn: () => fetchOrgMembers(organizationId as string),
    enabled: !!organizationId,
    // Pre-migration fetchMembers() had no retry wrapper.
    retry: false,
  });

  // Pre-migration cleared `allMembers` to [] on any fetch error (not just
  // "keep the last-known list") -- preserve that rather than letting
  // TanStack's default "retain previous data on error" behavior leak
  // through, which would silently show stale members after a failed reload.
  const allMembers: SelectOption[] = useMemo(() => {
    if (query.isError) return [];
    const memberData = query.data?.members || [];
    return memberData
      .filter((m: RawOrgMember) => !(excludeSelf && m.isSelf))
      .map((m: RawOrgMember) => ({
        id: m.userId,
        label: m.userName || m.displayName || 'Unknown',
        subLabel: m.email,
      }));
  }, [query.data, query.isError, excludeSelf]);

  // Filter out excluded IDs client-side (no re-fetch needed)
  const members = useMemo(
    () => allMembers.filter((m) => !stableExcludeIds.includes(m.id)),
    [allMembers, stableExcludeIds]
  );

  const reload = useCallback(async () => {
    await query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    members,
    loading: query.isLoading,
    error: query.isError ? 'Failed to load organization members' : '',
    reload,
  };
};
